import PropTypes from "prop-types";
import { useId, useState } from "react";

// material-ui
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

// ==============================|| NOTIFICACOES - DETALHE ||============================== //
//
// Dialog reutilizavel: a pagina de listagem e o sino do cabecalho usam o mesmo.
// Nao faz request: o GET /notifications/{id} devolve os mesmos 5 campos da lista
// e nao marca como lida, entao o item que o chamador ja tem em memoria basta.
//
// Contrato:
// - `notificacao`: item ja mapeado (utils/notificacoes). Nao ha data na API, por
//   isso o dialog nao mostra nenhuma.
// - `onIrParaRegistro(rota)`: chamado so quando `notificacao.rota` existe. Quem
//   usa decide o `navigate` e fecha o dialog; este componente nao fecha sozinho.
//   O `link` da API e uma URL absoluta da homologacao e nunca e usado aqui.
// - Marcar como lida ao abrir e responsabilidade de quem usa (o dialog so exibe).
//   Se `notificacao` for atualizada com o dialog aberto (ex.: virou lida), o chip
//   acompanha.
// - Ao fechar, o conteudo permanece durante a animacao de saida, mesmo que o
//   chamador passe `notificacao={null}` junto com `open={false}`.

// Compartilhado com a listagem: "Nao lida" em destaque e "Lida" neutra.
export function ChipSituacaoNotificacao({ lida }) {
  if (lida) {
    return (
      <Chip
        label="Lida"
        size="small"
        variant="outlined"
        sx={{
          color: "text.secondary",
          fontWeight: 400,
          fontSize: "12px",
          height: "28px",
        }}
      />
    );
  }

  return (
    <Chip
      label="Não lida"
      size="small"
      color="primary"
      sx={{ fontWeight: 600, fontSize: "12px", height: "28px" }}
    />
  );
}

ChipSituacaoNotificacao.propTypes = {
  lida: PropTypes.bool,
};

function CampoDetalhe({ rotulo, children }) {
  return (
    <Stack spacing={0.5} alignItems="flex-start">
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 600 }}
      >
        {rotulo}
      </Typography>
      {children}
    </Stack>
  );
}

CampoDetalhe.propTypes = {
  rotulo: PropTypes.string.isRequired,
  children: PropTypes.node,
};

function DetalheNotificacao({ open, notificacao, onClose, onIrParaRegistro }) {
  const tituloId = useId();

  // Guarda a ultima notificacao recebida para o conteudo nao sumir antes de a
  // animacao de saida terminar (padrao "ajustar estado durante o render": o
  // setState condicional so roda quando chega uma notificacao diferente).
  const [retida, setRetida] = useState(notificacao);
  if (notificacao && notificacao !== retida) setRetida(notificacao);

  const exibida = notificacao ?? retida;
  if (!exibida) return null;

  const { destaque, texto, lida, rota } = exibida;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby={tituloId}
    >
      <DialogTitle id={tituloId}>Detalhes da notificação</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <CampoDetalhe rotulo="Título">
            <Typography sx={{ wordBreak: "break-word" }}>
              {destaque || "—"}
            </Typography>
          </CampoDetalhe>

          <CampoDetalhe rotulo="Mensagem">
            <Typography sx={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
              {texto || "—"}
            </Typography>
          </CampoDetalhe>

          <CampoDetalhe rotulo="Situação">
            <ChipSituacaoNotificacao lida={lida} />
          </CampoDetalhe>

          {/* Sem rota nao ha botao; o texto evita procurar um registro que nao existe. */}
          {!rota && (
            <Typography variant="body2" color="text.secondary">
              Esta notificação não tem um registro vinculado.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
        {rota && (
          <Button variant="contained" onClick={() => onIrParaRegistro(rota)}>
            Ir para o registro
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

DetalheNotificacao.propTypes = {
  open: PropTypes.bool.isRequired,
  notificacao: PropTypes.shape({
    id: PropTypes.string,
    destaque: PropTypes.string,
    texto: PropTypes.string,
    lida: PropTypes.bool,
    rota: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  onIrParaRegistro: PropTypes.func.isRequired,
};

export default DetalheNotificacao;
